import {
  AlertCircle,
  Download,
  LoaderIcon,
  TrendingDown,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ReactApexChart from "react-apexcharts";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { productAnalyticsQueries } from "@/api/product-analytics";
import { useMemo, useState } from "react";
import { DateRange } from "@/lib/date";
import { ApexOptions } from "apexcharts";
import { productQueries } from "@/api/product";
import { safeParseDate } from "@/lib/utils";

const CHART_OPTIONS = {
  chart: {
    type: "line",
    toolbar: { show: false },
    zoom: { enabled: false },
  },
  xaxis: {
    type: "datetime",
    labels: {
      format: "MMM dd",
      style: {
        colors: "#ffffff", // or "white"
      },
    },
  },
  yaxis: {
    labels: {
      formatter: (val: number) => val.toLocaleString(),
      style: {
        colors: "#ffffff", // or "white"
      },
    },
    title: {
      text: "Credits / Calls",
      style: {
        color: "#ffffff", // or 'white'
      }, // or "white"
    },
  },
  tooltip: {
    theme: "dark",
    x: { format: "MMM dd, yyyy" },
    y: { formatter: (val: number) => val.toLocaleString() },
  },
  stroke: { curve: "smooth", width: 2 },
  legend: {
    show: true,
    position: "top",
    labels: {
      colors: "#ffffff",
    },
  },
  colors: ["#D1FA64", "#8884d8"],
} satisfies ApexOptions;

export function ProductOverview({
  productId,
  dateRange,
}: {
  productId: string;
  dateRange: DateRange;
}) {
  const [showApiCalls, setShowApiCalls] = useState(false);

  const { data: singleProductAnalytics, isLoading: isSingleLoading } = useQuery(
    productAnalyticsQueries.product(
      productId,
      dateRange.from?.toISOString(),
      dateRange.to?.toISOString(),
    ),
  );

  const { data: allProducts = [] } = useQuery(productQueries.products());
  const selectedProduct = allProducts.find((p) => p.product_sku === productId);

  const singleUsageSeries = useMemo(() => {
    if (!singleProductAnalytics?.daily_usage) return [];
    const baseSeries = {
      name: "Credits Used",
      data: singleProductAnalytics.daily_usage.map((d) => [
        safeParseDate(d.date)?.getTime() ?? Number.parseInt(d.date),
        d.credits ?? 0,
      ]),
    };
    if (showApiCalls) {
      const callsSeries = {
        name: "API Calls",
        data: singleProductAnalytics.daily_usage.map((d) => [
          safeParseDate(d.date)?.getTime() ?? Number.parseInt(d.date),
          d.calls ?? 0,
        ]),
      };
      return [baseSeries, callsSeries];
    } else {
      return [baseSeries];
    }
  }, [singleProductAnalytics, showApiCalls]);

  const handleExportData = () => {
    // If single product
    if (productId && singleProductAnalytics) {
      const csvRows = [
        ["Date", "Credits", "API Calls", "Avg Credits/Call"],
        ...singleProductAnalytics.daily_usage.map((day) => {
          const calls = day.calls ?? 0;
          const creds = day.credits ?? 0;
          return [
            day.date,
            creds.toString(),
            calls.toString(),
            calls > 0 ? (creds / calls).toFixed(2) : "0.00",
          ];
        }),
      ];
      const csvString = csvRows.map((r) => r.join(",")).join("\n");
      const blob = new Blob([csvString], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `product-analytics-${productId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  return (
    <>
      {isSingleLoading ? (
        <div className="flex items-center justify-center h-[300px]">
          <LoaderIcon className="h-8 w-8 animate-spin" />
        </div>
      ) : singleProductAnalytics ? (
        <>
          {/* Single-Product Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* #1: Total Credits */}
            <Card className="p-4">
              <CardContent className="p-0">
                <h4 className="text-sm font-medium text-muted-foreground">
                  Total Credits Used
                </h4>
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-2xl font-bold font-mono">
                    {singleProductAnalytics.summary.total_credits?.toLocaleString() ??
                      0}
                  </p>
                  <div className="flex items-center gap-1">
                    {(singleProductAnalytics.summary.growth_rate ?? 0) >= 0 ? (
                      <TrendingUp className="h-4 w-4 text-green-500" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-500" />
                    )}
                    <span
                      className={
                        (singleProductAnalytics.summary.growth_rate ?? 0) >= 0
                          ? "text-green-500"
                          : "text-red-500"
                      }
                    >
                      {Math.abs(
                        singleProductAnalytics.summary.growth_rate ?? 0,
                      ).toFixed(1)}
                      %
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* #2: Total Calls */}
            <Card className="p-4">
              <CardContent className="p-0">
                <h4 className="text-sm font-medium text-muted-foreground">
                  Total API Calls
                </h4>
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-2xl font-bold font-mono">
                    {singleProductAnalytics.summary.total_calls?.toLocaleString() ??
                      "0"}
                  </p>
                  <Badge>
                    {singleProductAnalytics.daily_usage?.length ?? 0} days
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* #3: Avg Credits/Call */}
            <Card className="p-4">
              <CardContent className="p-0">
                <h4 className="text-sm font-medium text-muted-foreground">
                  Avg Credits per Call
                </h4>
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-2xl font-bold font-mono">
                    {(
                      singleProductAnalytics.summary.average_credits_per_call ??
                      0
                    ).toFixed(2)}
                  </p>
                  <Badge variant="outline">Efficiency</Badge>
                </div>
              </CardContent>
            </Card>

            {/* #4: Unique Agents */}
            <Card className="p-4">
              <CardContent className="p-0">
                <h4 className="text-sm font-medium text-muted-foreground">
                  Active Users
                </h4>
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-2xl font-bold font-mono">
                    {singleProductAnalytics.summary.unique_agents?.toLocaleString() ??
                      0}
                  </p>
                  <Badge variant="secondary">
                    <Users className="h-3 w-3 mr-1" />
                    Users
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Single-Product Usage Chart */}
          <Card className="p-4 mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold">Usage Trends</h3>
                <p className="text-sm text-muted-foreground">
                  Product usage patterns and API call distribution
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowApiCalls(!showApiCalls)}
                >
                  {showApiCalls ? "Hide API Calls" : "Show API Calls"}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleExportData}
                  disabled={isSingleLoading}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="h-[400px] relative">
              {isSingleLoading ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <LoaderIcon className="h-8 w-8 animate-spin" />
                </div>
              ) : singleUsageSeries.length > 0 ? (
                <ReactApexChart
                  type="line"
                  series={singleUsageSeries}
                  options={CHART_OPTIONS}
                  height={400}
                />
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mb-2" />
                  <p>No usage data available</p>
                </div>
              )}
            </div>
          </Card>

          {/* Product Info & Usage Patterns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Product Info */}
            <Card className="p-4">
              <h3 className="text-lg font-semibold mb-4">
                Product Information
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Product Name</span>
                  <span className="font-medium">{selectedProduct?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">SKU</span>
                  <Badge variant="outline">
                    {selectedProduct?.product_sku}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Family</span>
                  <Badge>{selectedProduct?.product_family}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Credit Cost</span>
                  <Badge variant="secondary">
                    <Zap className="h-3 w-3 mr-1" />
                    {selectedProduct?.credit_cost} credits/call
                  </Badge>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-2">
                    Description
                  </span>
                  <p className="text-sm">{selectedProduct?.description}</p>
                </div>
              </div>
            </Card>

            {/* Usage Insights (peak usage, weekly, top users, etc.) */}
            <Card className="p-4">
              <h3 className="text-lg font-semibold mb-4">Usage Insights</h3>
              <div className="space-y-4">
                {/* Peak Usage Time */}
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">
                    Peak Usage Time
                  </h4>

                  {singleProductAnalytics.summary.peak_usage?.timestamp &&
                  !isNaN(
                    new Date(
                      singleProductAnalytics.summary.peak_usage.timestamp,
                    ).getTime(),
                  ) ? (
                    <div className="flex items-center justify-between">
                      <span className="text-sm">
                        {format(
                          new Date(
                            singleProductAnalytics.summary.peak_usage.timestamp,
                          ),
                          "MMM dd, yyyy HH:mm",
                        )}
                      </span>
                      <Badge variant="outline">
                        {(
                          singleProductAnalytics.summary.peak_usage.credits ?? 0
                        ).toLocaleString()}{" "}
                        credits
                      </Badge>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No valid peak usage date
                    </p>
                  )}
                </div>
                {/* Possibly Weekly Distribution, Top Users, etc. */}
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">
                    Weekly Distribution
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    (Add if aggregator provides weekly distribution)
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">
                    Top Users
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    (Add from aggregator if available)
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </>
      ) : (
        <div className="h-[300px] flex flex-col items-center justify-center text-muted-foreground">
          <AlertCircle className="h-8 w-8 mb-2" />
          <p>No data for this product in the selected range</p>
        </div>
      )}
    </>
  );
}
